import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import API from "@/lib/api";
import { Send, Loader2, MessageSquarePlus, ChevronDown, ChevronUp, Clock, CheckCircle } from "lucide-react";
import { toast } from "sonner";

export function RefinementPanel({ promptData, onRefined }) {
  const [input, setInput] = useState("");
  const [refining, setRefining] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const inputRef = useRef(null);
  const historyEndRef = useRef(null);

  const refinements = promptData?.refinements || [];

  useEffect(() => {
    if (historyEndRef.current) {
      historyEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [refinements.length]);

  const handleRefine = async (e) => {
    e.preventDefault();
    if (!input.trim() || !promptData?.id) return;
    setRefining(true);
    try {
      const { data } = await API.post(`/prompts/${promptData.id}/refine`, {
        additional_input: input.trim(),
      });
      setInput("");
      if (onRefined) onRefined(data);
      toast.success("Prompts refined", { description: "All prompt sections updated with your input." });
    } catch (err) {
      console.error("Refine error:", err);
    } finally {
      setRefining(false);
    }
  };

  if (!promptData?.id) return null;

  return (
    <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] mt-6" data-testid="refinement-panel">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between border-b border-[var(--pf-border)] px-5 py-3 hover:bg-[var(--pf-surface)] transition-colors duration-150"
        data-testid="refinement-toggle"
      >
        <div className="flex items-center gap-2">
          <MessageSquarePlus className="w-4 h-4 text-[var(--pf-accent)]" />
          <span className="font-heading text-sm font-bold">Refine Prompt</span>
          {refinements.length > 0 && (
            <span className="text-[10px] bg-[var(--pf-accent)] text-white px-1.5 py-0.5 font-medium">
              {refinements.length} {refinements.length === 1 ? "iteration" : "iterations"}
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-[var(--pf-text-muted)]" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[var(--pf-text-muted)]" />
        )}
      </button>

      {expanded && (
        <div className="p-5">
          {/* Refinement History */}
          {refinements.length > 0 && (
            <div className="mb-4">
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-3">
                Refinement History
              </p>
              <ScrollArea className="max-h-[280px]">
                <div className="space-y-3">
                  {refinements.map((r, idx) => (
                    <div
                      key={idx}
                      className="border-l-2 border-[var(--pf-accent)] pl-3 py-1"
                      data-testid={`refinement-entry-${idx}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-medium text-[var(--pf-accent)]">
                          Iteration {idx + 1}
                        </span>
                        <div className="flex items-center gap-1 text-[10px] text-[var(--pf-text-muted)]">
                          <Clock className="w-2.5 h-2.5" />
                          {new Date(r.timestamp).toLocaleString([], {
                            month: "short", day: "numeric",
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </div>
                      </div>
                      <p className="text-sm text-[var(--pf-text)] leading-relaxed">
                        {r.input}
                      </p>
                    </div>
                  ))}
                  <div ref={historyEndRef} />
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Input Area */}
          <form onSubmit={handleRefine}>
            <p className="text-xs text-[var(--pf-text-secondary)] mb-2">
              {refinements.length === 0
                ? "Add clarifications, revise features, or provide implementation details to refine the generated prompts."
                : "Continue refining - add more details, adjust requirements, or specify implementation changes."}
            </p>
            <div className="flex gap-2">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  refinements.length === 0
                    ? "e.g. Add user authentication with Google OAuth, include a dashboard with analytics charts..."
                    : "e.g. Also include role-based access control with admin and viewer roles..."
                }
                disabled={refining}
                data-testid="refinement-input"
                className="flex-1 rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] resize-none font-mono text-sm min-h-[72px]"
                rows={2}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    handleRefine(e);
                  }
                }}
              />
              <Button
                type="submit"
                disabled={refining || !input.trim()}
                data-testid="refine-button"
                className="self-end bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-10 px-4 font-medium transition-colors duration-150 shrink-0"
              >
                {refining ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-1.5" />
                    Refine
                  </>
                )}
              </Button>
            </div>
            <p className="text-[10px] text-[var(--pf-text-muted)] mt-1.5">
              Press Ctrl+Enter to submit
            </p>
          </form>
        </div>
      )}
    </div>
  );
}
